import { useEffect, useMemo } from "react";
import { useLocation } from "react-router";

import { useAppDispatch } from "store/hooks";
import { IbanInvoice } from "store/features/ibanInvoiceSlice";
import { IbanAccount } from "store/features/ibanAccountSlice";
import { setNotification } from "store/features/notificationSlice";

import { IbanKeys } from "shared/constants/banks";

export const useInvoiceAccountHandler = (
  invoice: IbanInvoice | null,
  accounts: IbanAccount[] | null,
  isExpired?: boolean,
  isLoading?: boolean,
) => {
  const dispatch = useAppDispatch();

  const { search } = useLocation();

  const params = new URLSearchParams(search);
  const uidParam = params.get("accountUid");

  const storageUid = localStorage.getItem(IbanKeys.PREFERRED_ACCOUNT_UID);

  const activeAccounts = useMemo(() => {
    if (!accounts || isExpired) {
      return [];
    }

    return accounts.filter((acc) => !acc.isSuspended);
  }, [accounts, isExpired]);

  const selectedAccount = useMemo(() => {
    if (isLoading) {
      return null;
    }

    if (invoice && invoice?.iban) {
      const matched = activeAccounts.find(
        (acc) => acc.ibanAccountUid === invoice.ibanAccountUid,
      );

      if (matched) {
        return matched;
      }

      const newAccountFromInvoice: IbanAccount = {
        id: "new",
        ibanAccountUid: "new",
        organizationName: invoice?.organizationName,
        identificationCode: invoice?.identificationCode,
        iban: invoice?.iban,
        publicName: invoice?.publicName,
        isSuspended: false,
        bank: {
          bankId: 0,
          name: "",
          mfoCode: "",
        },
      };
      return newAccountFromInvoice;
    }

    if (uidParam) {
      const matched = activeAccounts.find(
        (acc) => acc.ibanAccountUid === uidParam,
      );

      if (matched) {
        return matched;
      }
    }

    if (storageUid) {
      const matched = activeAccounts.find(
        (acc) => acc.ibanAccountUid === storageUid,
      );

      if (matched) {
        return matched;
      }
    }

    return activeAccounts[0] || null;
  }, [search, invoice, activeAccounts, isLoading]);

  useEffect(() => {
    if (invoice?.iban) return;

    if (
      uidParam &&
      activeAccounts.length > 0 &&
      !activeAccounts.find((acc) => acc.ibanAccountUid === uidParam)
    ) {
      dispatch(
        setNotification({
          severity: "error",
          message: "Вказаний рахунок не знайдено серед активних рахунків",
        }),
      );
    }
  }, [invoice, activeAccounts, search, dispatch]);

  return {
    activeAccounts,
    selectedAccount,
  };
};
